import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, Package, Users, Send, BarChart3, CheckCircle, Circle,
  ChevronRight, Play, RefreshCw, Mail, Edit3, FileText, Download,
  AlertTriangle, Eye, Zap, ArrowRight, XCircle, ChevronDown, ChevronUp,
  ShieldCheck, Clock, Star, TrendingDown, DollarSign, Trophy, Sparkles
} from "lucide-react";
import DealPacketBuilder from "../DealPacketBuilder";
import { downloadReport } from "../../utils/downloadReport";
import { fmtUSD } from "../../utils/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "package", label: "Compile Package", icon: Package, desc: "Build deal packet" },
  { key: "lenders", label: "Select Lenders", icon: Users, desc: "AI match + select" },
  { key: "send", label: "Review & Send", icon: Send, desc: "Email preview" },
  { key: "track", label: "Track Responses", icon: BarChart3, desc: "Monitor outreach" },
];

const BADGE_CONFIG = {
  best_overall: { icon: Trophy, label: "Best Overall", bg: "bg-gold/20 text-gold-dark border-gold/40" },
  lowest_rate: { icon: TrendingDown, label: "Lowest Rate", bg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  lowest_points: { icon: Star, label: "Lowest Points", bg: "bg-amber-50 text-amber-700 border-amber-200" },
  highest_loan: { icon: DollarSign, label: "Highest Loan", bg: "bg-blue-50 text-blue-700 border-blue-200" },
  fastest_close: { icon: Clock, label: "Fastest Close", bg: "bg-purple-50 text-purple-700 border-purple-200" },
};

export default function LenderOutreachWorkflow({ leadId, dealNumber }) {
  const [step, setStep] = useState("package");
  const [outreach, setOutreach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [sending, setSending] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [selectedLenders, setSelectedLenders] = useState([]);
  const [sendResult, setSendResult] = useState(null);
  const [quotes, setQuotes] = useState(null);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchOutreach = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/outreach/${leadId}`, { headers });
      if (res.data.exists) {
        setOutreach(res.data);
        setEmailSubject(res.data.email_draft?.subject || "");
        setEmailBody(res.data.email_draft?.body || "");
        const qualified = (res.data.lender_matches || [])
          .filter(m => m.qualified)
          .map(m => m.crm_id);
        setSelectedLenders(res.data.selected_lenders?.length ? res.data.selected_lenders : qualified);
      } else {
        setOutreach(null);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [leadId]);

  const fetchQuotes = useCallback(async () => {
    setQuotesLoading(true);
    try {
      const res = await axios.get(`${API}/outreach/${leadId}/ranked-offers`, { headers });
      if (res.data.total > 0) {
        setQuotes({ ranked: true, offers: res.data.offers, total: res.data.total });
      } else {
        const res2 = await axios.get(`${API}/outreach/${leadId}/quotes`, { headers });
        setQuotes({ ranked: false, quotes: res2.data.quotes, total: res2.data.total, ...res2.data });
      }
    } catch {
      try {
        const res2 = await axios.get(`${API}/outreach/${leadId}/quotes`, { headers });
        setQuotes({ ranked: false, quotes: res2.data.quotes, total: res2.data.total, ...res2.data });
      } catch (err2) { console.error(err2); }
    }
    finally { setQuotesLoading(false); }
  }, [leadId]);

  useEffect(() => { fetchOutreach(); }, [fetchOutreach]);
  useEffect(() => { if (step === "track") fetchQuotes(); }, [step, fetchQuotes]);

  const runPipeline = async () => {
    setRunning(true);
    setSendResult(null);
    try {
      await axios.post(`${API}/outreach/${leadId}/run-pipeline`, {}, { headers });
      await fetchOutreach();
      setStep("lenders");
    } catch (err) {
      console.error(err);
      alert("Pipeline error: " + (err.response?.data?.detail || err.message));
    }
    finally { setRunning(false); }
  };

  const saveEmail = async () => {
    try {
      await axios.put(`${API}/outreach/${leadId}/email`, { subject: emailSubject, body: emailBody }, { headers });
      setEditingEmail(false);
      await fetchOutreach();
    } catch (err) { console.error(err); }
  };

  const toggleLender = (crmId) => {
    setSelectedLenders(prev =>
      prev.includes(crmId) ? prev.filter(id => id !== crmId) : [...prev, crmId]
    );
  };

  const selectAllQualified = () => {
    const qualified = (outreach?.lender_matches || []).filter(m => m.qualified).map(m => m.crm_id);
    setSelectedLenders(qualified);
  };

  const handleSend = async () => {
    if (selectedLenders.length === 0) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/outreach/${leadId}/send`, {
        crm_ids: selectedLenders,
        subject: emailSubject,
        body: emailBody,
      }, { headers });
      setSendResult(res.data);
      await fetchOutreach();
      setStep("track");
    } catch (err) {
      console.error(err);
      alert("Send error: " + (err.response?.data?.detail || err.message));
    }
    finally { setSending(false); }
  };

  const recordResponse = async (crmId, responseType) => {
    try {
      await axios.post(`${API}/outreach/${leadId}/lender-response`, {
        crm_id: crmId,
        response_type: responseType,
      }, { headers });
      await fetchOutreach();
    } catch (err) { console.error(err); }
  };

  const selectWinner = async (crmId, lenderName) => {
    if (!window.confirm(`Select ${lenderName} as the winning lender? This will advance the deal.`)) return;
    setSelecting(true);
    try {
      const res = await axios.post(`${API}/outreach/${leadId}/select-winning-lender`, {
        winning_crm_id: crmId,
        reason: "Selected via quote comparison",
      }, { headers });
      alert(res.data.message);
      await fetchQuotes();
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    }
    finally { setSelecting(false); }
  };

  const isSent = outreach?.status === "sent";
  const matches = outreach?.lender_matches || [];
  const qualifiedMatches = matches.filter(m => m.qualified);
  const sends = outreach?.sends || [];

  // Determine step completion status
  const stepStatus = {
    package: "available",
    lenders: outreach ? "available" : "locked",
    send: outreach && selectedLenders.length > 0 ? "available" : "locked",
    track: isSent ? "available" : "locked",
  };
  if (isSent) {
    stepStatus.package = "done";
    stepStatus.lenders = "done";
    stepStatus.send = "done";
  } else if (outreach) {
    stepStatus.package = "done";
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="workflow-loading">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="lender-outreach-workflow">
      {/* Step Navigator */}
      <div className="flex items-center gap-1 p-1 rounded-xl bg-cream border border-cream-warm" data-testid="workflow-steps">
        {STEPS.map((s, i) => {
          const status = stepStatus[s.key];
          const isActive = step === s.key;
          const isDone = status === "done";
          const isLocked = status === "locked";
          const Icon = s.icon;

          return (
            <React.Fragment key={s.key}>
              {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-[#ccc] shrink-0" />}
              <button
                onClick={() => !isLocked && setStep(s.key)}
                disabled={isLocked}
                data-testid={`workflow-step-${s.key}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all flex-1 min-w-0 ${
                  isActive
                    ? "bg-emerald-700 text-white shadow-sm"
                    : isDone
                    ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                    : isLocked
                    ? "text-[#bbb] cursor-not-allowed"
                    : "text-[#666] hover:bg-white"
                }`}
              >
                {isDone && !isActive ? (
                  <CheckCircle className="h-3.5 w-3.5 shrink-0" />
                ) : (
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                )}
                <span className="truncate hidden sm:inline">{s.label}</span>
                <span className="truncate sm:hidden">{s.label.split(" ")[0]}</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {/* Step Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.15 }}
        >
          {/* Step 1: Compile Package */}
          {step === "package" && (
            <div className="space-y-5" data-testid="workflow-package-step">
              <DealPacketBuilder dealId={leadId} isInternal={true} />

              {/* Download + Run Pipeline */}
              <div className="grid sm:grid-cols-2 gap-3">
                <button
                  onClick={() => downloadReport(leadId, "deal-packet")}
                  className="flex items-center gap-3 p-4 rounded-xl border border-gold/30 bg-gold/5 hover:bg-gold/10 transition-colors text-left"
                  data-testid="workflow-download-packet"
                >
                  <FileText className="h-5 w-5 text-gold-dark" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-[#0A0A0A]">Download PDF</div>
                    <div className="text-[10px] text-[#888]">Branded deal packet{dealNumber && ` - ${dealNumber}`}</div>
                  </div>
                  <Download className="h-4 w-4 text-gold-dark" />
                </button>

                {!outreach ? (
                  <button
                    onClick={runPipeline}
                    disabled={running}
                    data-testid="workflow-run-pipeline"
                    className="flex items-center gap-3 p-4 rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors text-left"
                  >
                    {running ? <Loader2 className="h-5 w-5 animate-spin text-emerald-600" /> : <Zap className="h-5 w-5 text-emerald-600" />}
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-emerald-700">{running ? "Running..." : "Run AI Pipeline"}</div>
                      <div className="text-[10px] text-emerald-600">Underwrite, package, match lenders</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-emerald-600" />
                  </button>
                ) : (
                  <button
                    onClick={() => setStep("lenders")}
                    data-testid="workflow-next-lenders"
                    className="flex items-center gap-3 p-4 rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors text-left"
                  >
                    <CheckCircle className="h-5 w-5 text-emerald-600" />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-emerald-700">Pipeline Complete</div>
                      <div className="text-[10px] text-emerald-600">{qualifiedMatches.length} lenders matched</div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-emerald-600" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Select Lenders */}
          {step === "lenders" && outreach && (
            <div className="space-y-4" data-testid="workflow-lenders-step">
              {/* AI Summary */}
              {(outreach.underwriting?.executive_summary || outreach.packaging?.lender_pitch) && (
                <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4">
                  <h4 className="text-xs uppercase tracking-wider text-emerald-600 mb-2 flex items-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5" /> AI Analysis
                  </h4>
                  {outreach.underwriting?.executive_summary && (
                    <p className="text-sm text-[#333] mb-1">{outreach.underwriting.executive_summary}</p>
                  )}
                  {outreach.packaging?.lender_pitch && (
                    <p className="text-sm text-emerald-700 italic">{outreach.packaging.lender_pitch}</p>
                  )}
                </div>
              )}

              {/* Metrics Row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {[
                  { label: "Risk Score", value: outreach.underwriting?.risk_score || "N/A",
                    color: (outreach.underwriting?.risk_score || 0) >= 75 ? "text-emerald-600" : (outreach.underwriting?.risk_score || 0) >= 60 ? "text-amber-600" : "text-orange-600" },
                  { label: "Decision", value: (outreach.underwriting?.decision || "N/A").replace("_", " "), capitalize: true },
                  { label: "Matched", value: outreach.qualified_lender_count || 0 },
                  { label: "Selected", value: selectedLenders.length, color: selectedLenders.length > 0 ? "text-emerald-600" : "text-[#999]" },
                ].map(m => (
                  <div key={m.label} className="rounded-xl border border-cream-warm bg-cream p-3 text-center">
                    <div className="text-[9px] uppercase tracking-wider text-[#999] mb-0.5">{m.label}</div>
                    <div className={`text-lg font-bold ${m.color || "text-[#0A0A0A]"} ${m.capitalize ? "capitalize" : ""}`}>{m.value}</div>
                  </div>
                ))}
              </div>

              {/* Lender Selection */}
              <div className="rounded-xl border border-cream-warm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-cream border-b border-cream-warm">
                  <span className="text-sm font-bold text-[#333]">Matched Lenders ({qualifiedMatches.length} qualified)</span>
                  {!isSent && (
                    <button onClick={selectAllQualified} className="text-xs text-emerald-600 hover:underline font-semibold" data-testid="workflow-select-all">
                      Select all qualified
                    </button>
                  )}
                </div>
                <div className="max-h-[300px] overflow-y-auto divide-y divide-cream-warm">
                  {matches.length === 0 ? (
                    <p className="text-sm text-[#888] py-6 text-center">No CRM lenders available.</p>
                  ) : (
                    matches.map(m => (
                      <label key={m.crm_id} data-testid={`workflow-lender-${m.crm_id}`}
                        className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                          m.qualified ? "hover:bg-cream/50 cursor-pointer" : "opacity-50 cursor-not-allowed bg-slate-50/50"
                        }`}
                      >
                        {!isSent && (
                          <input
                            type="checkbox"
                            checked={selectedLenders.includes(m.crm_id)}
                            onChange={() => m.qualified && toggleLender(m.crm_id)}
                            disabled={!m.qualified || isSent}
                            className="rounded border-cream-warm text-emerald-600 focus:ring-emerald-400"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-[#333] truncate">{m.lender_name}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                              m.qualified ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"
                            }`}>
                              {m.fit_score}
                            </span>
                          </div>
                          <div className="text-[10px] text-[#888]">
                            {m.contact_name} {m.contact_email && `(${m.contact_email})`}
                          </div>
                          {m.reasons?.length > 0 && (
                            <div className="text-[10px] text-[#999] mt-0.5">{m.reasons.slice(0, 3).join(" · ")}</div>
                          )}
                        </div>
                        {isSent && (() => {
                          const send = sends.find(s => s.crm_id === m.crm_id);
                          if (!send) return null;
                          return (
                            <div className="flex items-center gap-1.5">
                              <SendStatusBadge status={send.status} />
                              {send.status === "sent" && (
                                <div className="flex gap-1">
                                  <button onClick={(e) => { e.preventDefault(); recordResponse(m.crm_id, "interested"); }}
                                    className="px-1.5 py-0.5 rounded text-[8px] bg-emerald-100 text-emerald-700 hover:bg-emerald-200">Int</button>
                                  <button onClick={(e) => { e.preventDefault(); recordResponse(m.crm_id, "passed"); }}
                                    className="px-1.5 py-0.5 rounded text-[8px] bg-red-100 text-red-600 hover:bg-red-200">Pass</button>
                                  <button onClick={(e) => { e.preventDefault(); recordResponse(m.crm_id, "term_sheet"); }}
                                    className="px-1.5 py-0.5 rounded text-[8px] bg-blue-100 text-blue-700 hover:bg-blue-200">TS</button>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        {!isSent && m.turn_time_days && (
                          <span className="text-[10px] text-[#999] shrink-0">{m.turn_time_days}d turn</span>
                        )}
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Re-run + Next */}
              <div className="flex gap-3">
                <button onClick={runPipeline} disabled={running}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-cream-warm text-sm font-medium text-[#555] hover:bg-cream transition-colors disabled:opacity-50"
                  data-testid="workflow-rerun-pipeline">
                  {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Re-run Pipeline
                </button>
                <button onClick={() => setStep("send")} disabled={selectedLenders.length === 0}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-emerald-700 text-white font-semibold hover:bg-emerald-600 disabled:opacity-40 transition-colors"
                  data-testid="workflow-next-send">
                  <Mail className="h-4 w-4" />
                  Review Email ({selectedLenders.length} lenders)
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Review & Send */}
          {step === "send" && outreach && (
            <div className="space-y-5" data-testid="workflow-send-step">
              {/* Send summary */}
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4">
                <div className="flex items-center gap-2 text-sm font-bold text-emerald-700 mb-1">
                  <Send className="h-4 w-4" />
                  Ready to send to {selectedLenders.length} lender{selectedLenders.length !== 1 ? "s" : ""}
                </div>
                <div className="text-xs text-[#888]">
                  {selectedLenders.map(id => {
                    const m = matches.find(l => l.crm_id === id);
                    return m?.lender_name || id;
                  }).join(", ")}
                </div>
              </div>

              {/* Lender Brief — from AI analysis */}
              {outreach.lender_brief && (
                <div className="rounded-xl border border-purple-100 bg-purple-50/30 p-4 space-y-2" data-testid="workflow-lender-brief">
                  <div className="text-[10px] uppercase tracking-wider text-purple-600 font-semibold flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3" /> AI Lender Brief (included in outreach)
                  </div>
                  {outreach.lender_brief.headline && (
                    <div className="text-sm font-bold text-[#222]">{outreach.lender_brief.headline}</div>
                  )}
                  {outreach.lender_brief.opportunity_summary && (
                    <p className="text-xs text-[#555] leading-relaxed">{outreach.lender_brief.opportunity_summary}</p>
                  )}
                  {outreach.lender_brief.key_highlights?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {outreach.lender_brief.key_highlights.map((h, i) => (
                        <span key={i} className="px-2 py-0.5 rounded text-[9px] font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">{h}</span>
                      ))}
                    </div>
                  )}
                  {outreach.lender_brief.considerations?.length > 0 && (
                    <div className="text-[10px] text-[#999] italic">Considerations: {outreach.lender_brief.considerations.join(" | ")}</div>
                  )}
                </div>
              )}

              {/* Email Preview/Edit */}
              <div className="rounded-xl border border-cream-warm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-cream border-b border-cream-warm">
                  <span className="text-sm font-bold text-[#333] flex items-center gap-2">
                    <Mail className="h-4 w-4 text-[#888]" /> Email Template
                  </span>
                  {!editingEmail && !isSent && (
                    <button onClick={() => setEditingEmail(true)} className="flex items-center gap-1 text-xs text-emerald-600 hover:underline font-semibold" data-testid="workflow-edit-email">
                      <Edit3 className="h-3 w-3" /> Edit
                    </button>
                  )}
                </div>
                <div className="p-4 space-y-3">
                  {editingEmail ? (
                    <>
                      <div>
                        <label className="text-xs text-[#888] mb-1 block">Subject</label>
                        <input value={emailSubject} onChange={e => setEmailSubject(e.target.value)}
                          className="w-full text-sm px-3 py-2 rounded-lg border border-cream-warm bg-white text-[#0A0A0A] focus:outline-none focus:border-emerald-400"
                          data-testid="workflow-email-subject" />
                      </div>
                      <div>
                        <label className="text-xs text-[#888] mb-1 block">Body</label>
                        <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)} rows={10}
                          className="w-full text-sm px-3 py-2 rounded-lg border border-cream-warm bg-white text-[#0A0A0A] font-mono leading-relaxed focus:outline-none focus:border-emerald-400 resize-y"
                          data-testid="workflow-email-body" />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={saveEmail} className="px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-600" data-testid="workflow-save-email">
                          Save Changes
                        </button>
                        <button onClick={() => { setEditingEmail(false); setEmailSubject(outreach.email_draft?.subject || ""); setEmailBody(outreach.email_draft?.body || ""); }}
                          className="px-4 py-2 rounded-lg border border-cream-warm text-sm text-[#666] hover:bg-cream">Cancel</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-[#999] mb-1">Subject</div>
                        <div className="text-sm font-medium text-[#0A0A0A]" data-testid="workflow-email-subject-preview">{emailSubject || "(No subject)"}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-[#999] mb-1">Body</div>
                        <div className="text-sm text-[#333] whitespace-pre-line leading-relaxed bg-cream rounded-lg p-3" data-testid="workflow-email-body-preview">
                          {emailBody || "(No email body generated yet)"}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Send Action */}
              <div className="flex gap-3">
                <button onClick={() => setStep("lenders")}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-cream-warm text-sm font-medium text-[#555] hover:bg-cream transition-colors"
                  data-testid="workflow-back-lenders">
                  Back to Lenders
                </button>
                <button onClick={handleSend} disabled={selectedLenders.length === 0 || sending || isSent}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-emerald-700 text-white font-semibold hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="workflow-approve-send">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {isSent ? "Already Sent" : `Approve & Send to ${selectedLenders.length} Lender${selectedLenders.length !== 1 ? "s" : ""}`}
                </button>
              </div>

              {/* Send Result */}
              {sendResult && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl border border-cream-warm p-4" data-testid="workflow-send-result">
                  {sendResult.total_sent > 0 && (
                    <div className="text-sm text-emerald-600 font-semibold flex items-center gap-1.5">
                      <CheckCircle className="h-4 w-4" /> Sent to {sendResult.total_sent} lender{sendResult.total_sent !== 1 ? "s" : ""}
                    </div>
                  )}
                  {sendResult.total_errors > 0 && (
                    <div className="text-sm text-red-500 mt-1">{sendResult.total_errors} failed</div>
                  )}
                </motion.div>
              )}
            </div>
          )}

          {/* Step 4: Track Responses */}
          {step === "track" && (
            <div className="space-y-5" data-testid="workflow-track-step">
              {/* Outreach Status */}
              {isSent && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm">
                    <CheckCircle className="h-5 w-5" />
                    Sent to {sends.length} lender{sends.length !== 1 ? "s" : ""}
                  </div>
                  <div className="text-xs text-emerald-600 mt-1">
                    {outreach?.sent_at && `Sent on ${new Date(outreach.sent_at).toLocaleString()}`}
                  </div>
                </div>
              )}

              {/* Response Tracker */}
              {sends.length > 0 && (
                <div className="rounded-xl border border-cream-warm overflow-hidden">
                  <div className="px-4 py-3 bg-cream border-b border-cream-warm">
                    <span className="text-sm font-bold text-[#333]">Lender Responses</span>
                  </div>
                  <div className="divide-y divide-cream-warm">
                    {sends.map(s => {
                      const m = matches.find(l => l.crm_id === s.crm_id);
                      return (
                        <div key={s.crm_id} className="flex items-center gap-3 px-4 py-3" data-testid={`workflow-send-${s.crm_id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-[#333]">{m?.lender_name || s.crm_id}</div>
                            <div className="text-[10px] text-[#888]">{m?.contact_email}</div>
                          </div>
                          <SendStatusBadge status={s.status} />
                          {s.status === "sent" && (
                            <div className="flex gap-1">
                              <button onClick={() => recordResponse(s.crm_id, "interested")}
                                className="px-2 py-1 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                data-testid={`response-interested-${s.crm_id}`}>Interested</button>
                              <button onClick={() => recordResponse(s.crm_id, "passed")}
                                className="px-2 py-1 rounded text-[9px] font-bold bg-red-100 text-red-600 hover:bg-red-200"
                                data-testid={`response-passed-${s.crm_id}`}>Passed</button>
                              <button onClick={() => recordResponse(s.crm_id, "term_sheet")}
                                className="px-2 py-1 rounded text-[9px] font-bold bg-blue-100 text-blue-700 hover:bg-blue-200"
                                data-testid={`response-ts-${s.crm_id}`}>Term Sheet</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quote Comparison */}
              <div className="rounded-xl border border-cream-warm overflow-hidden">
                <div className="px-4 py-3 bg-cream border-b border-cream-warm">
                  <span className="text-sm font-bold text-[#333]">Quote Comparison</span>
                </div>
                <div className="p-4">
                  {quotesLoading ? (
                    <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>
                  ) : !quotes || quotes.total === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="h-8 w-8 text-[#ccc] mx-auto mb-3" />
                      <p className="text-sm text-[#888]">No term sheets received yet.</p>
                      <p className="text-xs text-[#bbb] mt-1">They'll appear here as lenders respond with offers.</p>
                    </div>
                  ) : (
                    <QuoteTable quotes={quotes} selectWinner={selectWinner} selecting={selecting} />
                  )}
                </div>
              </div>

              {!isSent && (
                <div className="text-center py-4">
                  <p className="text-sm text-[#888] mb-3">No outreach sent yet. Complete the previous steps first.</p>
                  <button onClick={() => setStep("package")} className="text-sm text-emerald-600 font-semibold hover:underline">
                    Go to Step 1
                  </button>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function QuoteTable({ quotes, selectWinner, selecting }) {
  const offers = quotes.ranked ? quotes.offers : (quotes.quotes || []);
  return (
    <div className="space-y-4">
      {/* Best-in-class summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {(() => {
          const bestRate = offers.reduce((a, b) => (b.interest_rate && (!a || b.interest_rate < a)) ? b.interest_rate : a, null);
          const bestPts = offers.reduce((a, b) => (b.points !== null && b.points !== undefined && (!a || b.points < a)) ? b.points : a, null);
          const bestAmt = offers.reduce((a, b) => (b.loan_amount && (!a || b.loan_amount > a)) ? b.loan_amount : a, null);
          const bestClose = offers.reduce((a, b) => (b.closing_days && (!a || b.closing_days < a)) ? b.closing_days : a, null);
          return [
            { label: "Best Rate", value: bestRate != null ? `${bestRate}%` : "-", icon: TrendingDown, color: "text-emerald-600", bg: "bg-emerald-50" },
            { label: "Low Points", value: bestPts != null ? `${bestPts} pts` : "-", icon: Star, color: "text-amber-600", bg: "bg-amber-50" },
            { label: "High Offer", value: bestAmt != null ? fmtUSD(bestAmt) : "-", icon: DollarSign, color: "text-blue-600", bg: "bg-blue-50" },
            { label: "Fast Close", value: bestClose != null ? `${bestClose}d` : "-", icon: Clock, color: "text-purple-600", bg: "bg-purple-50" },
          ].map(s => (
            <div key={s.label} className={`rounded-lg border border-cream-warm ${s.bg} p-2.5 text-center`}>
              <s.icon className={`h-3.5 w-3.5 ${s.color} mx-auto mb-0.5`} />
              <div className={`text-sm font-bold ${s.color}`}>{s.value}</div>
              <div className="text-[8px] uppercase tracking-wider text-[#999]">{s.label}</div>
            </div>
          ));
        })()}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-cream-warm" data-testid="workflow-quote-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-cream">
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-[#888] font-semibold">Lender</th>
              {quotes.ranked && <th className="text-center px-2 py-2.5 text-[10px] uppercase tracking-wider text-[#888] font-semibold">Score</th>}
              <th className="text-center px-2 py-2.5 text-[10px] uppercase tracking-wider text-[#888] font-semibold">Rate</th>
              <th className="text-center px-2 py-2.5 text-[10px] uppercase tracking-wider text-[#888] font-semibold">Points</th>
              <th className="text-right px-2 py-2.5 text-[10px] uppercase tracking-wider text-[#888] font-semibold">Amount</th>
              <th className="text-center px-2 py-2.5 text-[10px] uppercase tracking-wider text-[#888] font-semibold">Close</th>
              <th className="text-left px-2 py-2.5 text-[10px] uppercase tracking-wider text-[#888] font-semibold">Badges</th>
              <th className="text-center px-2 py-2.5 text-[10px] uppercase tracking-wider text-[#888] font-semibold">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cream-warm">
            {offers.map((q, i) => {
              const badges = q.badges || [];
              const isBest = badges.includes("best_overall");
              return (
                <tr key={q.ts_id || i} className={`hover:bg-cream/50 ${isBest ? "bg-gold/5" : ""}`} data-testid={`workflow-quote-${q.ts_id || i}`}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      {isBest && <Trophy className="h-3.5 w-3.5 text-gold-dark" />}
                      <span className="font-semibold text-[#0A0A0A]">{q.lender_name}</span>
                    </div>
                  </td>
                  {quotes.ranked && (
                    <td className="px-2 py-2.5 text-center">
                      <span className={`font-bold ${q.offer_score >= 70 ? "text-emerald-600" : q.offer_score >= 50 ? "text-amber-600" : "text-orange-600"}`}>{q.offer_score}</span>
                    </td>
                  )}
                  <td className="px-2 py-2.5 text-center font-bold">{q.interest_rate != null ? `${q.interest_rate}%` : "-"}</td>
                  <td className="px-2 py-2.5 text-center font-bold">{q.points != null ? q.points : "-"}</td>
                  <td className="px-2 py-2.5 text-right font-bold">{q.loan_amount ? fmtUSD(q.loan_amount) : "-"}</td>
                  <td className="px-2 py-2.5 text-center">{q.closing_days ? `${q.closing_days}d` : "-"}</td>
                  <td className="px-2 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {badges.map(b => {
                        const cfg = BADGE_CONFIG[b];
                        if (!cfg) return null;
                        const Icon = cfg.icon;
                        return (
                          <span key={b} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-[8px] font-bold ${cfg.bg}`}>
                            <Icon className="h-2.5 w-2.5" /> {cfg.label}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    {q.crm_id && (
                      <button onClick={() => selectWinner(q.crm_id, q.lender_name)} disabled={selecting}
                        className="px-2.5 py-1 rounded-lg bg-emerald-700 text-white text-[10px] font-bold hover:bg-emerald-600 disabled:opacity-50"
                        data-testid={`workflow-select-winner-${q.crm_id}`}>
                        {selecting ? "..." : "Select"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-[#bbb] text-center">
        {quotes.total} term sheet{quotes.total !== 1 ? "s" : ""} received.
        {quotes.ranked && " Ranked by: 50% pricing, 25% close speed, 25% reliability."}
      </div>
    </div>
  );
}

function SendStatusBadge({ status }) {
  const map = {
    sent: { bg: "bg-blue-100 text-blue-700", label: "Sent" },
    interested: { bg: "bg-emerald-100 text-emerald-700", label: "Interested" },
    passed: { bg: "bg-red-100 text-red-600", label: "Passed" },
    term_sheet: { bg: "bg-purple-100 text-purple-700", label: "Term Sheet" },
    question: { bg: "bg-amber-100 text-amber-700", label: "Question" },
  };
  const cfg = map[status] || map.sent;
  return <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${cfg.bg}`}>{cfg.label}</span>;
}
